import {Component, OnDestroy, OnInit} from '@angular/core';
import {BaseService} from "../../services/base.service";
import {HttpClient} from "@angular/common/http";
import {Meta, Title} from "@angular/platform-browser";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {Favorites, FavoritesData} from "../../interfaces/favorites-data";
import {Router} from "@angular/router";
import * as moment from "moment";
import {cloneDeep} from 'lodash';
import {GoodsService} from "../../services/goods.service";

@Component({
  selector: 'app-favorites',
  templateUrl: './favorites.component.html',
  styleUrls: ['./favorites.component.scss']
})
export class FavoritesComponent extends BaseService implements OnDestroy, OnInit {
  public isDestroyed: Subject<boolean> = new Subject();
  public favorites: Favorites[] = [];
  // Таймер на кожну упаковку: зі спільним зміна кількості в іншому рядку протягом 800 мс
  // скасовувала збереження попереднього, і getData() повертав йому стару кількість.
  private timers: {[packId: string]: any} = {};

  public filterFavorites: string = '';
  public filteredFavorites: Favorites[] = [];
  public currentYear: number;

  constructor(
    private http: HttpClient,
    private titleService: Title,
    private meta: Meta,
    private router: Router,
    private goodsService: GoodsService,
  ) {
    super();

    const title = 'Маслоточка | Список побажань';
    this.titleService.setTitle(title);
    this.currentYear = moment().year();
    this.meta.addTag({ name: 'title', content: title });
    this.meta.addTag({ name: 'og:title', content: title });
    this.meta.addTag({ name: 'twitter:title', content: title });
    this.meta.addTag({ name: 'DC.title', content: title });
  }

  ngOnInit() {
    this.getData();
  }

  getData() {
    this.http.get(this.apiRoot + 'favorites/data')
      .pipe(takeUntil(this.isDestroyed))
      .subscribe((data: FavoritesData) => {
        if (data && Array.isArray(data.data)) {
          this.favorites = data.data;
        } else {
          this.favorites = [];
        }

        const pack_ids: number[] = [];

        if (this.favorites.length > 0) {
          for(let good of this.favorites) {
            good.available = parseInt(good.available);
            good.count = parseInt(good.count);

            if (good.waiting == '1') {
              pack_ids.push(Number(good.pack_id));
            }
          }
        }

        // Список перезавантажується після зміни кількості/видалення - зберігаємо введений пошук,
        // інакше поле пошуку показувало фільтр, а таблиця - весь список (або старі рядки, якщо дані не прийшли)
        this.searchFavorites();

        if (pack_ids.length) {
          this.http.post(this.apiRoot + 'good/get_arrivals_datas', {
            pack_ids
          })
            .pipe(takeUntil(this.isDestroyed))
            .subscribe((resp: any) => {
              const waitingDateByWar = this.goodsService.getArrivalsExpectByPack(resp?.data);

              for (const good of this.favorites) {
                const dt = good.waiting === '1' ? waitingDateByWar[Number(good.pack_id)] : null;
                good.waiting_expect = dt ? moment(dt.expect).format('DD.MM') : '';
                // позначка «інший склад: …», якщо товар очікується лише не на складі доставки клієнта (#446)
                good.waiting_expect_note = dt ? dt.note : '';
              }

              this.searchFavorites();
            });
        }
      });
  }

  searchFavorites() {
    const favorites  = cloneDeep(this.favorites);

    const searchTerm = this.filterFavorites.toLowerCase().trim();

    this.filteredFavorites = favorites.filter(good => {
      const name = good.name?.toLowerCase() || '';
      const article = good.article?.toLowerCase() || '';
      return name.includes(searchTerm) || article.includes(searchTerm);
    });
  }

  crossClearsSearchField() {
    this.filterFavorites = '';
    this.getData();
  }

  deleteFavorite(id) {
    this.http.post(this.apiRoot + 'favorites/delete', {'package_id': id})
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(() => {
        this.getData();
      });
  }

  goodCount(good: Favorites) {
    clearTimeout(this.timers[good.pack_id]);

    if (good.count === '' || good.count < 1) {
      good.count = 1;
    }

    this.timers[good.pack_id] = setTimeout(() => {
      delete this.timers[good.pack_id];

      this.http.post(this.apiRoot + 'favorites/set_count', {
        pack_id: good.pack_id,
        count: good.count
      })
        .pipe(takeUntil(this.isDestroyed))
        .subscribe(() => {
          this.getData();
        });
    }, 800);
  }

  minusCount(good: Favorites) {
    good.count = parseInt(good.count) - 1;

    this.goodCount(good);
  }

  plusCount(good: Favorites) {
    good.count = parseInt(good.count) + 1;

    this.goodCount(good);
  }

  goToGoods(good: Favorites) {
    this.router.navigate(['account'], {queryParams: {good_id: parseInt(good.pack_id)}});
  }

  clearAll() {
    // Тільки POST: по GET-посиланню бек обране більше не чистить (#400)
    this.http.post(this.apiRoot + 'favorites/delete_all', {})
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(() => {
        this.getData();
      });
  }

  ngOnDestroy() {
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
  }
}
